import { useCallback, useEffect, useState } from "react";
import {
  fetchGoogleSheets,
  removeGoogleSheet,
  type GoogleSpreadsheet,
} from "./googleSheetsApi";

interface UseGoogleSheetsOptions {
  loadOnMount?: boolean;
}

export const useGoogleSheets = (
  options: UseGoogleSheetsOptions = {},
): {
  sheets: GoogleSpreadsheet[];
  isLoading: boolean;
  errorMessage: string;
  removingFileIds: string[];
  fetchSheets: () => Promise<void>;
  removeSheet: (fileId: string) => Promise<void>;
  clearError: () => void;
} => {
  const { loadOnMount = false } = options;
  const [sheets, setSheets] = useState<GoogleSpreadsheet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [removingFileIds, setRemovingFileIds] = useState<string[]>([]);

  const fetchSheets = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const connectedSheets = await fetchGoogleSheets();
      setSheets(connectedSheets);
    } catch (error) {
      console.error("Failed to fetch connected spreadsheets:", error);
      setErrorMessage("Could not load connected spreadsheets.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeSheet = useCallback(
    async (fileId: string) => {
      setRemovingFileIds((prev) => [...prev, fileId]);
      setErrorMessage("");
      try {
        await removeGoogleSheet(fileId);
        await fetchSheets();
      } catch (error) {
        console.error("Failed to remove connected spreadsheet:", error);
        setErrorMessage("Could not remove spreadsheet.");
      } finally {
        setRemovingFileIds((prev) => prev.filter((id) => id !== fileId));
      }
    },
    [fetchSheets],
  );

  const clearError = useCallback(() => {
    setErrorMessage("");
  }, []);

  useEffect(() => {
    if (loadOnMount) {
      void fetchSheets();
    }
  }, [loadOnMount, fetchSheets]);

  return {
    sheets,
    isLoading,
    errorMessage,
    removingFileIds,
    fetchSheets,
    removeSheet,
    clearError,
  };
};
